// Passkey step-up + enrollment modals. Mirrors ConfirmDialog: an
// in-DOM modal over a dimmed backdrop, focus-trapped, dismissable by Escape /
// backdrop. The primary button runs a WebAuthn ceremony; the modal stays open
// on failure so the user can retry, and resolves true only on success.
//
// Desktop-only by design (: sensitive ops are desktop-only). On a
// phone/tablet — or a browser without passkey support — the action variant is
// replaced by an information-only variant with no ceremony button. This is the
// dual-UX adaptation, not a stretched layout: the desktop and phone renders are
// structurally different components for the same step.

import { ApiError } from '../services/api';
import { getDeviceClass } from '../utils/viewport';
import { enrollPasskey, isPasskeySupported, isStepUpRequired, stepUpWithPasskey } from '../services/webauthn';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface ActionSpec {
  title: string;
  message: string;
  actionLabel: string;
  progressLabel: string;
  cancelLabel: string;
  run: () => Promise<void>;
}

interface InfoSpec {
  title: string;
  message: string;
  dismissLabel: string;
}

// requestPasskeyStepUp prompts the user to verify with their passkey. Resolves
// true once the assertion succeeds (the session is now stepped up), false if
// the user dismisses. On a non-desktop / unsupported client it shows why the
// operation can't proceed and resolves false.
export function requestPasskeyStepUp(): Promise<boolean> {
  if (getDeviceClass() !== 'desktop' || !isPasskeySupported()) {
    return infoModal({
      title: 'Desktop required',
      message:
        'Sensitive operations need a desktop signed in with a passkey. Switch to your desktop to continue.',
      dismissLabel: 'OK',
    });
  }
  return actionModal({
    title: 'Verify it’s you',
    message: 'This action needs a passkey check. Use your fingerprint, face, or security key to continue.',
    actionLabel: 'Use passkey',
    progressLabel: 'Waiting for your passkey…',
    cancelLabel: 'Cancel',
    run: stepUpWithPasskey,
  });
}

// promptPasskeyEnroll offers to set up a passkey on first desktop sign-in for a
// sensitive role. Resolves true once enrolled, false if declined or not on a
// desktop (phones are exempt — no prompt).
export function promptPasskeyEnroll(): Promise<boolean> {
  if (getDeviceClass() !== 'desktop' || !isPasskeySupported()) {
    return Promise.resolve(false);
  }
  return actionModal({
    title: 'Set up a passkey',
    message:
      'Your role can approve certifications and run reports. Add a passkey so those actions stay protected — it takes a few seconds.',
    actionLabel: 'Set up passkey',
    progressLabel: 'Setting up your passkey…',
    cancelLabel: 'Not now',
    run: enrollPasskey,
  });
}

// withPasskeyStepUp wraps a sensitive API call: if the server gates it with
// 401 WEBAUTHN_STEP_UP_REQUIRED, it runs the assertion ceremony and retries
// once. Any other error — or a dismissed prompt — propagates unchanged. This is
// the call site every certification / export / AI-report action will use.
export async function withPasskeyStepUp<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (err) {
    if (!isStepUpRequired(err)) throw err;
    const ok = await requestPasskeyStepUp();
    if (!ok) throw err;
    return await call();
  }
}

function actionModal(spec: ActionSpec): Promise<boolean> {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const overlay = document.createElement('div');
  overlay.className = 'passkey';

  const dialog = document.createElement('div');
  dialog.className = 'passkey__dialog';
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');

  const title = document.createElement('h2');
  title.className = 'passkey__title';
  title.textContent = spec.title;

  const message = document.createElement('p');
  message.className = 'passkey__message';
  message.textContent = spec.message;

  const error = document.createElement('p');
  error.className = 'passkey__error';
  error.hidden = true;

  const actions = document.createElement('div');
  actions.className = 'passkey__actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'passkey__btn passkey__btn--cancel';
  cancel.textContent = spec.cancelLabel;

  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'passkey__btn passkey__btn--action';
  action.textContent = spec.actionLabel;

  actions.append(cancel, action);
  dialog.append(title, message, error, actions);
  overlay.append(dialog);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let running = false;

    function close(result: boolean): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
      resolve(result);
    }

    async function onAction(): Promise<void> {
      if (running) return;
      running = true;
      error.hidden = true;
      action.disabled = true;
      cancel.disabled = true;
      action.textContent = spec.progressLabel;
      try {
        await spec.run();
        close(true);
      } catch (err) {
        running = false;
        action.disabled = false;
        cancel.disabled = false;
        action.textContent = spec.actionLabel;
        error.textContent = ceremonyError(err);
        error.hidden = false;
      }
    }

    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !running) {
        e.preventDefault();
        close(false);
        return;
      }
      if (e.key === 'Tab') trapTab(e, dialog);
    }

    cancel.addEventListener('click', () => {
      if (!running) close(false);
    });
    action.addEventListener('click', () => void onAction());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && !running) close(false);
    });
    document.addEventListener('keydown', onKeydown, true);

    document.body.append(overlay);
    action.focus();
  });
}

function infoModal(spec: InfoSpec): Promise<boolean> {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const overlay = document.createElement('div');
  overlay.className = 'passkey';
  overlay.dataset.variant = 'info';

  const dialog = document.createElement('div');
  dialog.className = 'passkey__dialog';
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');

  const title = document.createElement('h2');
  title.className = 'passkey__title';
  title.textContent = spec.title;

  const message = document.createElement('p');
  message.className = 'passkey__message';
  message.textContent = spec.message;

  const actions = document.createElement('div');
  actions.className = 'passkey__actions';

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'passkey__btn passkey__btn--action';
  dismiss.textContent = spec.dismissLabel;

  actions.append(dismiss);
  dialog.append(title, message, actions);
  overlay.append(dialog);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    function close(): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
      resolve(false);
    }
    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'Tab') {
        trapTab(e, dialog);
      }
    }
    dismiss.addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKeydown, true);
    document.body.append(overlay);
    dismiss.focus();
  });
}

function trapTab(e: KeyboardEvent, dialog: HTMLElement): void {
  const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !(el as HTMLButtonElement).disabled,
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const activeEl = document.activeElement as HTMLElement | null;
  if (e.shiftKey && activeEl === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && activeEl === last) {
    e.preventDefault();
    first.focus();
  }
}

function ceremonyError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') return 'Passkey prompt was cancelled or timed out. Try again.';
    if (err.name === 'InvalidStateError') return 'This device already has a passkey for your account.';
    return 'Your authenticator couldn’t complete the request. Try again.';
  }
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? 'Verification failed. Try again.';
  }
  return 'Something went wrong. Try again.';
}
